import type { ComponentProps } from "react";
import Link from "next/link";
import { renderRichText } from "@storyblok/react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import { parseBerlinDate } from "@/lib/utils/berlinDate";
import { Button } from "@/components/ui/button";
import { Headline } from "@/components/typography/Headline";
import { RichText } from "@/components/typography/RichText";
import { cn } from "@/lib/utils";
import type { EventStoryblok } from "@/component-types-sb";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type EventProps = {
  event: EventStoryblok;
  image?: string;
  reverse?: boolean;
} & ComponentProps<"article">;

const defaultImage = "/images/shape4.svg";

const Event = ({
  event,
  image = defaultImage,
  reverse = true,
  ...rest
}: EventProps) => {
  const href = getUrlFromLinkObject(event.link) || "#";
  const date = parseBerlinDate(event.date);
  const weekday = format(date, "EEEE", { locale: de });
  const formattedDate = format(date, "dd. MMM yyyy", { locale: de });

  return (
    <article
      className={cn(
        "bg-white text-black-95 rounded-3xl flex flex-col-reverse md:flex-row gap-0 md:gap-14 mb-8 card-rotate relative cursor-pointer",
        {
          ["md:flex-row-reverse"]: reverse,
        },
        rest.className,
      )}
    >
      <div className="md:w-2/3 flex p-6 lg:p-8 flex-col items-start md:justify-center">
        <h3 className="text-subline  mb-6 md:my-4 font-bold">{event.title}</h3>

        <div className="hidden md:block">
          <RichText
            html={renderRichText(event.text)}
            className="text-copy my-4 line-clamp-5"
          />
        </div>

        <div className="w-full md:w-auto relative z-20">
          <Button variant="sun" className="w-full md:w-auto" asChild>
            <Link href={href}>{event.cta || "Mehr erfahren"}</Link>
          </Button>
        </div>
      </div>
      <div className="md:w-1/3 relative">
        <div className="h-40 md:h-full md:max-h-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={image} alt="" className="w-full h-full rounded-2xl" />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <Headline size="tall" as="p">
            {weekday}
            <br />
            {formattedDate}
          </Headline>
        </div>
      </div>
      <Link
        href={href}
        className="absolute inset-0 z-10"
        aria-label={event.title || "Weitere Informationen"}
      />
    </article>
  );
};

export { Event };
